<script setup lang="ts">
import {computed, watch} from "vue";
import {useUrlSearchParams} from "@vueuse/core";
import type {IStoreBannerDefault} from "@/api/aj-trends.model.ts";
import {useSearchParam} from "@/service/searchInputParam.service.ts";
import {usePaginationParams} from "@/service/paginationParams.service.ts";
import {provideSearchPageReload} from "@/service/searchPageReload.service.ts";
import {provideLoadingIndicator} from "@/service/loadingIndicator.service.ts";
import {fetchHtmlPage, type HtmlPageResult} from "@/service/fetchHtmlPage.ts";
import StoreBannerTag from "@/components/StoreBannerTag.vue";
import SearchPageBase from "@/components/SearchPageBase.vue";

provideLoadingIndicator()
const {reloadFirstPage} = provideSearchPageReload()

const props = defineProps<{ dataBanners: string }>()
const banners = computed(() => JSON.parse(props.dataBanners) as ReadonlyArray<IStoreBannerDefault>)

const urlSearchParams = useUrlSearchParams<Record<string, string>>('history', {removeFalsyValues: true})

const {search, searchInput, submitSearch} = useSearchParam(computed({
  get: () => urlSearchParams.q || '',
  set: q => urlSearchParams.q = q,
}))

const activeBannerIds = computed<number[]>({
  get: () => (urlSearchParams.b || '').split(',').map(x => parseInt(x) || 0).filter(x => x),
  set: b => urlSearchParams.b = b.map(x => String(x)).join(','),
})

function toggleBannerId(id: number) {
  activeBannerIds.value = activeBannerIds.value.includes(id)
      ? activeBannerIds.value.filter(x => x !== id)
      : activeBannerIds.value.concat(id)
}

const {pageNumber, pageSize} = usePaginationParams(urlSearchParams, {defaultPageSize: 20})

watch([search, activeBannerIds], reloadFirstPage)

function loadStores(): Promise<HtmlPageResult> {
  const params = new URLSearchParams()
  params.set('_page', String(pageNumber.value))
  params.set('_size', String(pageSize.value))

  if (search.value) params.set('q', search.value)
  if (activeBannerIds.value.length > 0) params.set('b', activeBannerIds.value.join(','))

  return fetchHtmlPage('/api/stores', params)
}
</script>

<template>
  <SearchPageBase
      search-placeholder="Suche nach Märkten"
      v-model:page-number="pageNumber"
      v-model:page-size="pageSize"
      v-model:search="searchInput"
      :load-page="loadStores"
      @submit-search="submitSearch"
  >
    <template #searchForm>
      <ul class="u-list-inline text-center">
        <li v-for="banner in banners" :key="banner.id" class="list-inline-item g-mb-10">
          <StoreBannerTag
              :banner="banner"
              :active="activeBannerIds.includes(banner.id)"
              @click.prevent="toggleBannerId(banner.id)"
          />
        </li>
      </ul>
    </template>
    <template #noneFound>
      <h2>Keine Märkte gefunden</h2>
    </template>
  </SearchPageBase>
</template>
